import type { EquipmentPurpose } from '@/types/equipment'
import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { equipmentApi } from '@/api/equipmentApi'
import { useMockApi } from '@/lib/useMock'
import { useTenant } from '@/providers/TenantProvider'
import { SEED_GROUP_NAMES } from '@/types/equipment'

/** A tenant equipment group — identified by id, displayed/matched by name. */
export type EquipmentGroupRow = { id: string; name: string }

export function groupsQueryKey(tenantId: string | null) {
  return ['equipment-groups', tenantId] as const
}

export type GroupMutations = {
  addGroup: (name: string) => Promise<void>
  renameGroup: (id: string, name: string) => Promise<void>
  removeGroup: (id: string) => Promise<void>
}

/**
 * Tenant-scoped equipment groups plus create/rename/delete mutations. Groups are
 * the source of truth for the planner's group filter and pickers. Mutations are
 * optimistic and refetch on both success and failure so backend errors (e.g. a
 * 409 on deleting a non-empty group) surface to the caller and roll the cache
 * back to server truth.
 */
export function useGroups(
  /**
   * This page's groups only (z8nrz7ewt2): on the Cleaning page (`['ROOM']`)
   * Room Groups, elsewhere equipment groups. A group added here belongs to it.
   */
  purposes?: readonly EquipmentPurpose[],
): {
  groups: EquipmentGroupRow[]
  isLoading: boolean
  error: string | null
  /** Resolve a group name to its id from the cached list (undefined if unknown). */
  resolveGroupId: (name: string) => string | undefined
} & GroupMutations {
  const queryClient = useQueryClient()
  const { selectedTenantId } = useTenant()
  const tenantId = useMockApi ? 'mock' : selectedTenantId

  const scope = purposes?.length ? purposes.join(',') : 'all'
  // The Cleaning page's groups are Room Groups — and so is every group made there.
  const forRooms = !!purposes?.length && purposes.every((p) => p === 'ROOM')
  // Under the shared prefix, so invalidating groupsQueryKey(tenant) refreshes both pages.
  const queryKey = useMemo(() => [...groupsQueryKey(tenantId), scope], [tenantId, scope])

  const query = useQuery({
    queryKey,
    enabled: tenantId !== null,
    queryFn: async (): Promise<EquipmentGroupRow[]> => {
      if (useMockApi) return SEED_GROUP_NAMES.map((name, i) => ({ id: `seed-${i}`, name }))
      return equipmentApi.listGroups(tenantId as string, purposes)
    },
  })

  const refetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  )

  const setGroups = useCallback(
    (update: (prev: EquipmentGroupRow[]) => EquipmentGroupRow[]) =>
      queryClient.setQueryData<EquipmentGroupRow[]>(queryKey, (prev) => update(prev ?? [])),
    [queryClient, queryKey],
  )

  const resolveGroupId = useCallback(
    (name: string): string | undefined =>
      queryClient.getQueryData<EquipmentGroupRow[]>(queryKey)?.find((g) => g.name === name)?.id,
    [queryClient, queryKey],
  )

  const addGroup = useCallback<GroupMutations['addGroup']>(
    async (name) => {
      setGroups((prev) => [...prev, { id: `temp-${name}`, name }])
      if (useMockApi || !tenantId) return
      try {
        await equipmentApi.createGroup(tenantId, { name, ...(forRooms ? { forRooms } : {}) })
      } catch (err) {
        await refetch()
        throw err
      }
      await refetch()
    },
    [forRooms, refetch, setGroups, tenantId],
  )

  const renameGroup = useCallback<GroupMutations['renameGroup']>(
    async (id, name) => {
      setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, name } : g)))
      if (useMockApi || !tenantId) return
      try {
        await equipmentApi.updateGroup(tenantId, id, { name })
      } catch (err) {
        await refetch()
        throw err
      }
      await refetch()
    },
    [refetch, setGroups, tenantId],
  )

  const removeGroup = useCallback<GroupMutations['removeGroup']>(
    async (id) => {
      setGroups((prev) => prev.filter((g) => g.id !== id))
      if (useMockApi || !tenantId) return
      try {
        await equipmentApi.deleteGroup(tenantId, id)
      } catch (err) {
        await refetch()
        throw err
      }
      await refetch()
    },
    [refetch, setGroups, tenantId],
  )

  return {
    groups: query.data ?? [],
    isLoading: query.isLoading || tenantId === null,
    error: query.error instanceof Error ? query.error.message : null,
    resolveGroupId,
    addGroup,
    renameGroup,
    removeGroup,
  }
}
